/**
 * Presentation helpers for Process Visibility step lists.
 *
 * PV keeps EVERY per-attempt step forever (compliance traceability; steps
 * are never deleted or expired), so long async transcriptions accumulate
 * many repeated poll steps. Collapsing happens here, at render time only:
 * the stored data is untouched.
 */

/**
 * Merge consecutive steps with the same operation+status into one display
 * group. Input order is preserved (oldest first, as the dialog renders).
 * Each group: { op, step (first), lastStep (latest), count }.
 */
export function groupConsecutiveSteps(steps) {
  const groups = [];
  for (const step of steps || []) {
    const op = step.operation || step.type || step.action || 'Step';
    const prev = groups[groups.length - 1];
    if (prev && prev.op === op && prev.step.status === step.status) {
      prev.count += 1;
      prev.lastStep = step;
    } else {
      groups.push({ op, step, lastStep: step, count: 1 });
    }
  }
  return groups;
}

/**
 * Compact one-line summary of transcription metadata on a step, or null
 * when the step carries none (non-transcription steps render unchanged).
 */
export function transcriptionMetaLine(metadata) {
  if (!metadata) return null;
  const parts = [];
  if (metadata.path === 'gpu') parts.push('GPU');
  else if (metadata.path === 'cpu') parts.push('CPU');
  if (metadata.model) parts.push(String(metadata.model));
  if (metadata.stage) parts.push(`stage: ${metadata.stage}`);
  if (metadata.audioDurationSeconds) {
    parts.push(`${(Number(metadata.audioDurationSeconds) / 60).toFixed(1)} min media`);
  }
  if (metadata.realtimeFactor) parts.push(`${metadata.realtimeFactor}x realtime`);
  if (metadata.permanent === true) parts.push('permanent failure');
  return parts.length > 0 ? parts.join(' · ') : null;
}
